import type { DiceTruthCommand } from '../shared/dice-publication';
import { protocolVersion } from '../../../tools/websocket/protocol.cjs';
import type { TruthPush } from '../shared/applied-truth-transport';
import { APPLIED_FRAME_MAX_BYTES, expandAppliedFrameInputs } from '../janus-engine/transport/applied-frame';
import { APPLIED_FRAME_MAX_PARTS, compressedAppliedFrameParts } from '../janus-engine/transport/applied-frame-parts';
import { ws } from './state';
import type { EphemeralTruth } from '../shared/ephemeral-truth';
import { crc32 } from '../encounter-minimap/layout';
import { campaignTruthPresentation, presentTruthCommand, truthTeleportOffers } from './truth-presentation';
import { encounterField } from '../shared/array-types';
import { ftCurrentTruthSink } from '../shared/truth-sink';

const ready = new WeakSet<WebSocket>();
const ephemeral = new Set<string>();
type Operation = { created: number; wire?: string; channel?: string; delivered?: boolean; saved?: boolean;
    queued?: boolean; denied?: boolean; publish?: () => void | Promise<void>; expiry?: ReturnType<typeof setTimeout> };
const operations = new Map<string, Operation>();
const OPERATION_MAX_AGE_MS = 30000;
function dropOperation(id: string, expired = false): void {
    const op = operations.get(id);
    if (!op) return;
    if (expired && !op.delivered && !op.denied && op.wire) counters.expired++;
    clearTimeout(op.expiry);
    releaseAppliedParts(id);
    operations.delete(id);
}
function pruneOperations(): void {
    const cutoff = Date.now() - OPERATION_MAX_AGE_MS;
    for (const [id, op] of operations) if (op.created <= cutoff) dropOperation(id, true);
    while (operations.size > 512) dropOperation(operations.keys().next().value!, true);
}
function operation(id: string): Operation {
    let op = operations.get(id);
    if (!op) {
        op = { created: Date.now() };
        operations.set(id, op);
        op.expiry = setTimeout(() => { dropOperation(id, true); drainOutbox(); }, OPERATION_MAX_AGE_MS);
    }
    pruneOperations();
    return op;
}
// Keep only ids for actor-side echo/render dedup after releasing saved frame bytes and closures.
const owned = new Set<string>();
const failedSaves = new Map<string, { operation_id: string; encounter_id: string; actor_id: string }>();
const received = new Set<string>(), corrections = new Set<string>();
const counters = { sent: 0, received: 0, nacked: 0, expired: 0, correctionsReceived: 0, bridgeVersionMismatch: 0 };
window.ftTruthTransportCounters = counters;
function rememberOwnOperation(id: string): void {
    owned.add(id);
    if (owned.size > 512) owned.delete(owned.values().next().value!);
}
const accountField = (key: string): boolean => /geoplugin/i.test(key)
    || ['player_email', 'player_email_updates', 'gm_email', 'campaign_invite_code'].includes(key);
// Same account-only scrub as publishToRedis; private/hidden game rows remain intact.
export function truthWireBytes(value: unknown): string {
    return JSON.stringify(value, (key, value: unknown) => {
        if (accountField(key)) return undefined;
        if (Array.isArray(value)) return value.filter(entry => !accountDelta(entry));
        return accountDelta(value) ? undefined : value;
    });
}
function accountDelta(value: unknown): boolean {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
    const row = value as Record<string, unknown>;
    const path = typeof row.path === 'string' ? row.path.split('.') : Array.isArray(row.path) ? row.path : [];
    return path.length > 0 && Object.keys(row).every(key => ['path', 'value', 'removed', 'op'].includes(key))
        && accountField(String(path[path.length - 1] ?? ''));
}
function applyTruthNotices(command: Record<string, unknown> | undefined): void {
    if (!command) return;
    // Local sinks present their saved rows; the command presenter serves the ordinary transport.
    if (!ftCurrentTruthSink()) void presentTruthCommand(command).catch(error => console.warn('[truth-transport] presentation failed', error));
    const offers = truthTeleportOffers(command);
    if (offers.length) window.dispatchEvent(new CustomEvent('ft:browser-teleport-offers', {
        detail: { encounterId: command.encounter_id, offers },
    }));
}
function publishCampaignPresentation(command: TruthPush['command']): void {
    if (command.schema === 'janus.card.edit.v1' && (!Array.isArray(command.truth_presentation_rows) || !command.truth_presentation_rows.length)) return;
    const encounter = window.FateTide?.encounterCache?.get(command.encounter_id);
    if (!encounter && !command.campaign_id) return;
    const campaignId = String(command.campaign_id ?? encounterField(encounter, 'encounter_master_parent_id') ?? '');
    if (campaignId && (window.FateTide?.campaignCache?.has(campaignId) || command.schema === 'janus.spell_prep.v1')) window.ftPushCampaignTruth?.({
        type: 'janus_truth_presentation', campaign_id: campaignId, operation_id: `${command.operation_id}:presentation`,
        presentation: campaignTruthPresentation(command),
    });
}
function releaseSavedOperation(id: string): void {
    const op = operations.get(id);
    if (!op?.delivered) return;
    if (op.saved) dropOperation(id);
}
function socketBytes(encounterId: string, wire: string): string {
    return `{"type":"transport_truth","channel":"encounter:${encounterId}","frame":${wire}}`;
}
window.ftPushEphemeralTruth = (frame: EphemeralTruth): boolean => {
    if (!ws || ws.readyState !== WebSocket.OPEN || !ready.has(ws)) return false;
    try {
        const userId = String(window.FateTide?.user?.id ?? '');
        const palette = ['#e74c3c', '#3498db', '#2ecc71', '#f39c12', '#9b59b6', '#1abc9c', '#e91e63', '#00bcd4'];
        const presentation = frame.type.startsWith('ft3d_move_preview')
            ? { from_user_id: userId, color: palette[crc32(userId) % palette.length] } : {};
        const channel = frame.transport_scope === 'campaign' && typeof frame.campaign_id === 'string'
            ? `campaign:${frame.campaign_id}` : `encounter:${frame.encounter_id}`;
        ws.send(JSON.stringify({ type: 'transport_truth', channel,
            frame: JSON.parse(truthWireBytes({ ...frame, ...presentation, truth: true })) }));
        if (frame.applied_frame) rememberOwnOperation(frame.operation_id);
        ephemeral.add(frame.operation_id);
        if (ephemeral.size > 512) ephemeral.delete(ephemeral.values().next().value!);
        counters.sent++;
        return true;
    } catch { return false; }
};
function sendOperation(id: string): void {
    const op = operations.get(id);
    if (!op || op.denied || op.delivered || !op.wire || !op.channel) return;
    op.queued = true;
    // Compression and paced multipart delivery keep their operation's queue slot.
    for (const [priorId, prior] of operations) {
        if (priorId === id) break;
        if (prior.queued && prior.wire && !prior.denied && !prior.delivered) return;
    }
    if (!ws || ws.readyState !== WebSocket.OPEN || !ready.has(ws)) return;
    const large = largeOperations.get(id);
    if (large) { queueAppliedParts(id); return; }
    try {
        ws.send(`{"type":"transport_truth","channel":${JSON.stringify(op.channel)},"frame":${op.wire}}`);
        op.queued = false;
        counters.sent++;
    } catch { /* Keep the immutable bytes until transport_ready. */ }
}
function drainOutbox(): void {
    for (const [id, op] of operations) if (op.queued) {
        sendOperation(id);
        if (op.queued) break;
    }
}
export function consumeTruthTransportControl(socket: WebSocket, control: { type?: string; truthRelay?: boolean; operation_id?: string; reason?: string; protocolVersion?: number }): boolean {
    if (['transport_ready', 'transport_capability'].includes(control.type ?? '') && (control.protocolVersion ?? 1) < protocolVersion) {
        if (!counters.bridgeVersionMismatch) console.warn('[websocket] Bridge protocol is older than this bundle; using shared features.');
        counters.bridgeVersionMismatch++;
    }
    if (control.type === 'transport_ready') {
        if (control.truthRelay === true) ready.add(socket); else ready.delete(socket);
        if (socket === ws && ready.has(socket)) {
            pruneOperations();
            // Map insertion order is action order, including uncertain sends on the old socket.
            drainOutbox();
            drainAppliedParts();
        }
        return true;
    }
    if (control.type !== 'transport_truth_nack') return false;
    if (socket === ws && control.operation_id && ephemeral.delete(control.operation_id)) {
        counters.nacked++;
        window.dispatchEvent(new CustomEvent('ft:ephemeral-truth-refused', { detail: control }));
    }
    if (socket === ws && control.operation_id && operations.has(control.operation_id)) {
        const op = operations.get(control.operation_id)!;
        counters.nacked++;
        if (['denied', 'oversize', 'already-answered'].includes(control.reason ?? '')) {
            op.denied = true; op.queued = false;
            releaseAppliedParts(control.operation_id);
            drainOutbox();
        } else op.queued = true;
    }
    return true;
}
export function truthSocketClosed(socket: WebSocket): void {
    ready.delete(socket);
    if (socket === ws) {
        for (const operation_id of ephemeral)
            window.dispatchEvent(new CustomEvent('ft:ephemeral-truth-refused', { detail: { operation_id, reason: 'socket-closed' } }));
        ephemeral.clear();
    }
    pruneOperations();
    // Frames queued at a closing relay may never receive a nack.
    if (socket === ws) for (const op of operations.values()) if (!op.delivered && !op.denied) op.queued = true;
    if (socket === ws) for (const state of largeOperations.values()) state.pending = false;
    if (socket === ws) partQueue.length = 0;
}
type LargeOperation = { parts: string[]; pending: boolean };
const largeOperations = new Map<string, LargeOperation>();
const partQueue: { id: string; index: number }[] = [];
let partPump: ReturnType<typeof setTimeout> | undefined;
function releaseAppliedParts(id: string): void {
    largeOperations.delete(id);
}
function drainAppliedParts(): void {
    if (!partQueue.length || partPump !== undefined || !ws || ws.readyState !== WebSocket.OPEN || !ready.has(ws)) return;
    // Leave room for ordinary actions under the bridge's 128-message/s limit.
    let budget = 8;
    while (partQueue.length && budget > 0) {
        const job = partQueue[0]!, state = largeOperations.get(job.id), op = operations.get(job.id);
        if (!state || !op || op.denied || op.delivered || corrections.has(job.id)) {
            partQueue.shift(); if (state) state.pending = false; continue;
        }
        try { ws.send(state.parts[job.index]!); budget--; job.index++; }
        catch { state.pending = false; partQueue.shift(); op.queued = true; break; }
        if (job.index === state.parts.length) {
            partQueue.shift(); state.pending = false; op.queued = false; counters.sent++;
        }
    }
    // Hold the slot even after a short batch, so concurrent actions share the cap.
    partPump = setTimeout(() => { partPump = undefined; if (partQueue.length) drainAppliedParts(); }, 100);
    drainOutbox();
}
function queueAppliedParts(id: string): void {
    const state = largeOperations.get(id);
    if (!state || state.pending || !state.parts.length) return;
    state.pending = true; partQueue.push({ id, index: 0 }); drainAppliedParts();
}
function pushLargeAppliedTruth(wire: string, frame: TruthPush['applied_frame'], installLocally = true, sourceType?: string): void | Promise<void> {
    const id = frame.operation_id, op = operations.get(id);
    if (!op) return;
    largeOperations.set(id, { parts: [], pending: false });
    op.wire = wire; op.channel = `encounter:${frame.encounter_id}`; op.queued = true;
    if (!('cache_ref' in frame) && frame.inputs?.new_entity_base) expandAppliedFrameInputs(frame);
    const localInstall = installLocally ? window.ftApplyBasePatch?.({ type: 'base_patch', encounter_id: frame.encounter_id, ops: [], applied: frame }) : undefined;
    void compressedAppliedFrameParts(wire, frame).then(parts => {
        const state = largeOperations.get(id);
        if (!state || operations.get(id) !== op || op.delivered || op.denied) return;
        if (parts.length > APPLIED_FRAME_MAX_PARTS) return;
        const bytes = parts.map(part => socketBytes(frame.encounter_id, JSON.stringify(['janus_zone_truth', 'janus_trap_truth'].includes(sourceType ?? '')
            ? { ...part, source_type: sourceType } : part)));
        if (bytes.some(part => new TextEncoder().encode(part).byteLength > APPLIED_FRAME_MAX_BYTES)) return;
        state.parts = bytes;
        sendOperation(id);
    }).catch(error => console.warn('[truth-transport] frame encoding failed', error));
    return localInstall;
}
function awaitingClaimedFrame(command: TruthPush['command']): boolean {
    const saving = operations.get(command.operation_id);
    const savedTime = command.schema === 'janus.time.push.v1' && (command.mode === 'combat' || command.mode === 'calendar');
    return command.committed === true && (!!command.timed_finish || savedTime) && saving?.saved === true && !saving.wire;
}
window.ftPushAppliedTruth = ({ type, command, applied_frame }: TruthPush): void | Promise<void> => {
    pruneOperations();
    const id = command.operation_id;
    const saving = operations.get(id);
    // Timed summons and clock pushes publish after saving, so their save owns the
    // id before their first frame exists. Ordinary optimistic lanes stay deduped.
    if ((owned.has(id) || saving) && !awaitingClaimedFrame(command)) return;
    rememberOwnOperation(id);
    if (!saving) operation(id);
    pruneOperations();
    try {
        applied_frame = window.ftPrepareAppliedDefinitionInputs?.(applied_frame) ?? applied_frame;
        // Encode borrowed computed references in the apply task, before another
        // action can mutate them. Never give live objects to a socket/iframe.
        const frame = { type, actor_id: applied_frame.actor_id, encounter_id: command.encounter_id, operation_id: id,
            truth: true, command: { ...command, presentation_user_id: window.FateTide?.user?.id, committed: true }, applied_frame };
        const wire = truthWireBytes(frame);
        const publish = (): void | Promise<void> => {
            const decoded = JSON.parse(wire) as TruthPush, applied = decoded.applied_frame;
            const installLocally = type !== 'janus_zone_truth' || window.ftZoneTruthMayInstall?.(decoded.command) !== false;
            applyTruthNotices(decoded.command);
            window.dispatchEvent(new CustomEvent('ft:truth-frame-applied', { detail: applied }));
            const bytes = socketBytes(command.encounter_id, wire);
            if (new TextEncoder().encode(bytes).byteLength > APPLIED_FRAME_MAX_BYTES) {
                const install = pushLargeAppliedTruth(wire, applied, installLocally, type);
                publishCampaignPresentation(decoded.command);
                return install;
            }
            const op = operations.get(id);
            if (!op) return;
            op.wire = wire; op.channel = `encounter:${command.encounter_id}`;
            sendOperation(id);
            publishCampaignPresentation(decoded.command);
            // Keep the actor's held bases/memos aligned too; its save echo must not recompile them.
            if (installLocally)
                return window.ftApplyBasePatch?.({ type: 'base_patch', encounter_id: command.encounter_id, ops: [], applied });
        };
        // Retain only the encoded decision delta. A refused turn cannot advance
        // saved-base revisions and make its participant repair look obsolete.
        if (command.schema === 'janus.turn.end.v1') { operations.get(id)!.publish = publish; return; }
        return publish();
    } catch (error) { console.warn('[truth-transport] encode failed', error); }
};
window.ftPushSavedSpellPrep = command => {
    pruneOperations();
    const id = command.operation_id;
    const prior = operations.get(id);
    if (prior?.wire || prior?.delivered || owned.has(id) && !prior) return;
    rememberOwnOperation(id);
    const op = prior ?? operation(id);
    op.wire = truthWireBytes({ type: 'janus_spell_prep_truth', truth: true,
        actor_id: command.actor_id, encounter_id: command.encounter_id, operation_id: id,
        command: { ...command, presentation_user_id: window.FateTide?.user?.id } });
    op.channel = command.encounter_id ? `encounter:${command.encounter_id}` : `caster:${command.actor_id}`;
    op.saved = true;
    sendOperation(id);
    const saved = JSON.parse(op.wire) as { command: TruthPush['command'] };
    applyTruthNotices(saved.command);
    if ((saved.command.rows as unknown[] | undefined)?.length) publishCampaignPresentation(saved.command);
};
window.ftSaveAppliedTruth = async (id, send) => {
    pruneOperations();
    rememberOwnOperation(id);
    const op = operation(id);
    let response: Response;
    try { response = await send(); }
    catch (error) { dropOperation(id); throw error; }
    op.saved = response.ok;
    if (op.saved && failedSaves.has(id)) {
        queueEncounterHeal(failedSaves.get(id)!, 'save-recovered');
        failedSaves.delete(id);
    }
    if (op.saved && op.publish) {
        const publish = op.publish;
        delete op.publish;
        if (operations.get(id) === op) await publish();
    }
    if (!response.ok) { delete op.publish; dropOperation(id); }
    else releaseSavedOperation(id);
    return response;
};
// Supplemental campaign deltas have their own delivery identity, so an encounter
// viewer subscribed to both channels does not discard the campaign clock update.
window.ftPushCampaignTruth = frame => {
    const id = frame.operation_id;
    if (owned.has(id)) return;
    rememberOwnOperation(id);
    if (frame.type === 'janus_truth_presentation') applyTruthNotices(frame.presentation as Record<string, unknown>);
    else applyTruthNotices(frame.command as Record<string, unknown> | undefined);
    const op = operation(id);
    op.wire = truthWireBytes({ ...frame, truth: true });
    op.channel = `campaign:${frame.campaign_id}`;
    op.saved = true;
    sendOperation(id);
};
// These lanes publish their canonical saved log identities, including a lost-answer replay.
// Existing operations retain the bytes until the socket echo acknowledges delivery.
window.ftPushSavedDiceTruth = (command: DiceTruthCommand, applied_frame): void | Promise<void> => {
    const id = command.operation_id, prior = operations.get(id);
    if (prior?.wire || received.has(id) || corrections.has(id)) return;
    const encounterId = String(command.encounter_id ?? ''), campaignId = String(command.campaign_id ?? '');
    const actorId = String(command.actor_id ?? command.caster_id ?? '');
    if (applied_frame) applied_frame = (window as Window & { ftPrepareAppliedDefinitionInputs?: (frame: TruthPush['applied_frame']) => TruthPush['applied_frame'] }).ftPrepareAppliedDefinitionInputs?.(applied_frame) ?? applied_frame;
    const channel = encounterId ? `encounter:${encounterId}` : campaignId ? `campaign:${campaignId}` : `user:${window.FateTide?.user?.id}`;
    rememberOwnOperation(id);
    const op = operation(id);
    const decided = { ...command, actor_id: actorId, encounter_id: encounterId, campaign_id: campaignId, presentation_user_id: window.FateTide?.user?.id };
    op.wire = truthWireBytes({ type: 'janus_dice_truth', truth: true, operation_id: id, actor_id: actorId,
        encounter_id: encounterId, campaign_id: campaignId, command: decided, ...(applied_frame ? { applied_frame } : {}) });
    op.channel = channel; op.saved = true;
    if (new TextEncoder().encode(JSON.stringify({ type: 'transport_truth', channel, frame: JSON.parse(op.wire) })).byteLength > APPLIED_FRAME_MAX_BYTES) {
        const wire = op.wire, state = { parts: [] as string[], pending: false };
        largeOperations.set(id, state); op.queued = true;
        void compressedAppliedFrameParts(wire, { operation_id: id, actor_id: actorId, encounter_id: encounterId }).then(parts => {
            if (operations.get(id) !== op || largeOperations.get(id) !== state || op.denied || op.delivered || parts.length > APPLIED_FRAME_MAX_PARTS) return;
            state.parts = parts.map(part => JSON.stringify({ type: 'transport_truth', channel, frame: { ...part,
                dice: { schema: String(command.schema), kind: String(command.kind), campaign_id: campaignId } } }));
            sendOperation(id);
        }).catch(error => console.warn('[dice-truth] frame encoding failed', error));
    } else sendOperation(id);
    applyTruthNotices(decided);
    if (encounterId) publishCampaignPresentation(decided as TruthPush['command']);
    if (applied_frame) {
        window.dispatchEvent(new CustomEvent('ft:truth-frame-applied', { detail: applied_frame }));
        return window.ftApplyBasePatch?.({ type: 'base_patch', encounter_id: encounterId, ops: [], applied: applied_frame });
    }
};
function queueEncounterHeal(command: { operation_id: string; encounter_id: string; actor_id: string }, reason: string): void {
    if (!command.encounter_id || !command.actor_id) return;
    const id = `${command.operation_id}:${reason}`;
    if (owned.has(id)) return;
    rememberOwnOperation(id);
    const op = operation(id);
    op.wire = truthWireBytes({ type: 'base_patch', truth: true, correction: true, operation_id: id,
        actor_id: command.actor_id, encounter_id: command.encounter_id, ops: [], resync: [command.encounter_id] });
    op.channel = `encounter:${command.encounter_id}`;
    op.saved = true;
    sendOperation(id);
    void window.ftResyncEncounter?.(command.encounter_id);
}
window.ftTruthSaveFailed = command => {
    dropOperation(command.operation_id);
    // Outer actor/exchange outboxes may later retry this same operation. Keep only
    // its routing identity so a successful recovery heals peers from saved truth again.
    failedSaves.set(command.operation_id, { operation_id: command.operation_id, encounter_id: command.encounter_id, actor_id: command.actor_id });
    if (failedSaves.size > 512) failedSaves.delete(failedSaves.keys().next().value!);
    queueEncounterHeal(command, 'save-failed');
};
// U0 exposes the one memoized result at each lane's local-render boundary.
// Reading it here keeps state in this bundle and leaves the action outbox alone.
for (const [event, type] of [['ability', 'janus_ability_truth'], ['attack', 'janus_attack_truth'], ['combat', 'janus_combat_truth'], ['movement', 'janus_movement_truth'], ['turn', 'janus_turn_truth']]) {
    window.addEventListener(`ft:${event}-truth-${event === 'combat' ? 'local-result' : 'applied'}`, raw => {
        const detail = (raw as CustomEvent<{ command: TruthPush['command']; applied_frame?: TruthPush['applied_frame'] }>).detail;
        if (!detail?.command) return;
        const getter = Object.getOwnPropertyDescriptor(detail, 'applied_frame');
        if (getter?.enumerable) Object.defineProperty(detail, 'applied_frame', { ...getter, enumerable: false });
        if (ownsTruthOperation(detail.command.operation_id) && !awaitingClaimedFrame(detail.command)) return;
        const frame = detail.applied_frame;
        if (!frame) return;
        const actorId = detail.command.actor_id || frame.actor_id;
        window.ftPushAppliedTruth?.({ type: type!, applied_frame: frame, command: { ...detail.command,
            actor_id: actorId, rev: frame.member_revs[actorId] ?? frame.rev, member_revs: frame.member_revs,
            ...(event === 'turn' ? { next_rev: frame.member_revs[String(detail.command.next_id)] } : {}),
            ...(event === 'attack' ? { target_rev: frame.member_revs[String(detail.command.target_id)] } : {}) } });
    });
}
/** Count/dedup the socket copy before a lane paints. Corrections keep their
 * existing base_patch resync on every page, including the initiating page. */
export function admitTruthWire(frame: Record<string, unknown>): boolean {
    const command = frame.command as Record<string, unknown> | undefined;
    const id = String(frame.operation_id ?? command?.operation_id ?? '');
    if (frame.type === 'janus_truth_presentation' && id.startsWith('encounter-environment:') && !frame.presentation)
        frame.presentation = command;
    if (frame.correction === true || command?.correction === true) {
        if (id && corrections.has(id)) return false;
        if (id) corrections.add(id);
        counters.correctionsReceived++;
        if (corrections.size > 512) corrections.delete(corrections.values().next().value!);
    }
    const own = operations.get(id);
    ephemeral.delete(id);
    if (own) releaseAppliedParts(id);
    if (own && (frame.correction === true || command?.correction === true)) dropOperation(id);
    else if (own) { own.delivered = true; own.queued = false; releaseSavedOperation(id); }
    if (frame.correction === true || command?.correction === true) return true;
    if (frame.truth !== true && command?.truth !== true) return true;
    if (received.has(id) || corrections.has(id)) return false;
    received.add(id); counters.received++;
    if (received.size > 512) received.delete(received.values().next().value!);
    if (frame.type === 'janus_truth_presentation') applyTruthNotices((frame.presentation ?? command) as Record<string, unknown>);
    else if (!ownsTruthOperation(id)) applyTruthNotices(command);
    return true;
}
export const ownsTruthOperation = (id: string): boolean => owned.has(id) || operations.has(id);
